import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductDetailsModal = ({ product, onClose, onEdit }) => {
  const getStatusColor = (status) => {
    const colors = {
      'In Stock': 'bg-success/10 text-success border-success/20',
      'Low Stock': 'bg-warning/10 text-warning border-warning/20',
      'Out of Stock': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const totalValue = product?.price * product?.quantity;
  const profitMargin = ((product?.price - product?.cost) / product?.price * 100)?.toFixed(1);

  const getTrackableLabel = (isTrackable) => {
    return isTrackable ? 'Trackable (Inventoried)' : 'Non-Trackable';
  };

  const getTrackableColor = (isTrackable) => {
    return isTrackable ? 'text-primary' : 'text-muted-foreground';
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Package" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                Product Details
              </h2>
              <p className="text-sm text-muted-foreground">{product?.sku}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                {product?.name}
              </h3>
              <p className="text-muted-foreground">{product?.description}</p>
            </div>
            <span
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(
                product?.status
              )}`}
            >
              {product?.status}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-muted-foreground">Pricing</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Selling Price:</span>
                  <span className="text-sm font-semibold text-foreground">
                    {formatCurrency(product?.price)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Cost:</span>
                  <span className="text-sm font-semibold text-foreground">
                    {formatCurrency(product?.cost)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="text-sm text-muted-foreground">Profit Margin:</span>
                  <span className="text-sm font-semibold text-success">{profitMargin}%</span>
                </div>
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Package" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium text-muted-foreground">Inventory</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Tracking:</span>
                  <span className={`text-sm font-semibold ${getTrackableColor(product?.isTrackable)}`}>
                    {getTrackableLabel(product?.isTrackable)}
                  </span>
                </div>
                {product?.isTrackable && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Quantity:</span>
                      <span className="text-sm font-semibold text-foreground">{product?.quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Reorder Level:</span>
                      <span className="text-sm font-semibold text-foreground">
                        {product?.reorderLevel}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-border">
                      <span className="text-sm text-muted-foreground">Total Value:</span>
                      <span className="text-sm font-semibold text-success">
                        {formatCurrency(totalValue)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-heading font-semibold text-foreground">Product Information</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <span className="text-sm font-medium text-muted-foreground">Category</span>
                <p className="text-foreground">{product?.category}</p>
              </div>

              <div className="space-y-1">
                <span className="text-sm font-medium text-muted-foreground">Unit Type</span>
                <p className="text-foreground font-medium">{product?.unit}</p>
              </div>

              <div className="space-y-1">
                <span className="text-sm font-medium text-muted-foreground">Supplier</span>
                <p className="text-foreground">{product?.supplier}</p>
              </div>

              <div className="space-y-1">
                <span className="text-sm font-medium text-muted-foreground">SKU</span>
                <p className="text-foreground font-mono">{product?.sku}</p>
              </div>

              {product?.isTrackable && (
                <div className="space-y-1">
                  <span className="text-sm font-medium text-muted-foreground">Last Restocked</span>
                  <p className="text-foreground">{formatDate(product?.lastRestocked)}</p>
                </div>
              )}
            </div>
          </div>

          {product?.isTrackable && product?.quantity <= product?.reorderLevel && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                <div>
                  <h5 className="font-semibold text-warning mb-1">Low Stock Alert</h5>
                  <p className="text-sm text-muted-foreground">
                    Current stock ({product?.quantity}) is at or below the reorder level (
                    {product?.reorderLevel}). Consider restocking soon.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button variant="default" iconName="Edit" onClick={onEdit}>
            Edit Product
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;